// Points Transfer API Service

import axios from "axios";
import { BASE_URL } from '../../../../Constants/EnvironmentVariableProvider';
import {
  POINTS_TRANSFER_API,
  SEARCH_AGENTS_API,
  TRANSFER_POINTS_API,
  GET_POINTS_TRANSFERS_API
} from '../../../../API/paths';

// 1. Search Agents
export async function searchAgents(searchTerm) {
  const res = await axios.get(
    `${BASE_URL}${POINTS_TRANSFER_API}${SEARCH_AGENTS_API}`,
    { params: { searchTerm } }
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to search agents");
  return result.data;
}

// 2. Transfer Points
export async function transferPoints({ fromAgentID, toAgentID, points, notes, submissionTransactionID }) {
  // Convert to PascalCase for backend
  const payload = {
    FromAgentID: fromAgentID,
    ToAgentID: toAgentID,
    Points: points,
    Notes: notes,
    SubmissionTransactionID: submissionTransactionID,
  };
  const res = await axios.post(
    `${BASE_URL}${POINTS_TRANSFER_API}${TRANSFER_POINTS_API}`,
    payload
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to transfer points");
  return result;
}

// 3. Get Points Transfers (with optional search)
export async function getPointsTransfers(filters = {}) {
  // Convert filters object to query string
  const params = {};
  if (filters.search) params.search = filters.search;
  if (filters.dateRange && filters.dateRange.length === 2) {
    // Format dates to include time for proper filtering
    params.startDate = filters.dateRange[0]?.format?.("YYYY-MM-DD HH:mm:ss") || filters.dateRange[0];
    params.endDate = filters.dateRange[1]?.format?.("YYYY-MM-DD HH:mm:ss") || filters.dateRange[1];
  }
  if (filters.fromAgent) params.fromAgent = filters.fromAgent;
  if (filters.toAgent) params.toAgent = filters.toAgent;
  if (filters.transferredBy) params.transferredBy = filters.transferredBy;
  if (filters.ruleId) params.ruleId = filters.ruleId;
  // Add pagination params
  if (filters.page) params.page = filters.page;
  if (filters.pageSize) params.pageSize = filters.pageSize;
  const res = await axios.get(
    `${BASE_URL}${POINTS_TRANSFER_API}${GET_POINTS_TRANSFERS_API}`,
    { params }
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to fetch transfers");
  return result.data;
}

// 4. Search Admins (for Transferred By autocomplete)
export async function searchAdmins(searchTerm) {
  const res = await axios.get(
    `${BASE_URL}Admins/DropdownSearch`,
    { params: { search: searchTerm } }
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to search admins");
  return result.data;
}

// 5. Get Single Transfer by ID
export async function getTransferById(transferId) {
  const res = await axios.get(
    `${BASE_URL}${POINTS_TRANSFER_API}GetTransferById/${transferId}`
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to fetch transfer");
  return result.data;
}

// 6. Get Agent Submission Transactions
export async function getAgentSubmissionTransactions(agentId) {
  const res = await axios.get(
    `${BASE_URL}${POINTS_TRANSFER_API}GetAgentSubmissionTransactions`,
    { params: { agentId } }
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to fetch submission transactions");
  return result.data;
}

// 7. Reverse Transaction
export async function reverseTransaction(transactionId) {
  const res = await axios.post(
    `${BASE_URL}${POINTS_TRANSFER_API}ReverseTransaction`,
    { transactionId }
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to reverse transaction");
  return result.data;
}

// 8. Get Agent Submissions (for submission sharing)
export async function getAgentSubmissions(agentId) {
  const res = await axios.get(
    `${BASE_URL}${POINTS_TRANSFER_API}GetAgentSubmissions`,
    { params: { agentId } }
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to fetch submissions");
  return result.data;
}

// 9. Manual Transfer
export async function manualTransfer({ fromAgentID, toAgentID, points, notes }) {
  const payload = {
    FromAgentID: fromAgentID,
    ToAgentID: toAgentID,
    Points: points,
    Notes: notes,
  };
  const res = await axios.post(
    `${BASE_URL}${POINTS_TRANSFER_API}ManualTransfer`,
    payload
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to transfer points");
  return result;
}

// 10. Submission Credit Transfer
export async function submissionCreditTransfer({ submissionID, shareholderAgentID, shareholderPercentage, notes }) {
  const payload = {
    SubmissionID: submissionID,
    ShareholderAgentID: shareholderAgentID,
    ShareholderPercentage: shareholderPercentage,
    Notes: notes,
  };
  const res = await axios.post(
    `${BASE_URL}${POINTS_TRANSFER_API}SubmissionCreditTransfer`,
    payload
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to share submission");
  return result;
}

// 11. Get Related Transactions (for navigation between original and reversed)
export async function getRelatedTransactions(transferId) {
  const res = await axios.get(
    `${BASE_URL}${POINTS_TRANSFER_API}GetRelatedTransactions/${transferId}`
  );
  const result = res.data;
  if (!result.success) throw new Error(result.message || "Failed to fetch related transactions");
  return result.data;
} 